import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronRight, Plus, RefreshCw } from "lucide-react";
import { verificationForms as formsApi } from "../../api/endpoints";
import { ConfirmState } from "../../components/feedback/ConfirmModal";
import { AddToast } from "../../components/feedback/Toast";
import { Btn } from "../../components/ui/Btn";
import { PageHeader } from "../../components/ui/PageHeader";
import { F, T } from "../../theme/tokens";
import { FieldModal } from "./FieldModal";
import { SectionCard } from "./SectionCard";
import { SectionModal } from "./SectionModal";
import { blankField, FormField, FormSection, FormSubject, formStats, move, newId, VerificationForm } from "./types";

type Drafts = Record<FormSubject, FormSection[]>;

const TABS: { id: FormSubject; label: string }[] = [
  { id: "talent", label: "Talent Verification" },
  { id: "organisation", label: "Organisation Verification" },
];

type Dialog =
  | { kind: "section"; sectionId: string | null }
  | { kind: "field"; sectionId: string; field: FormField | null };

// ── Verification forms ───────────────────────────────────────────────────────
// What a talent or an organisation has to provide to get verified. Both forms are
// edited as local drafts and written with Save Changes (one request per form, which
// bumps its version). The app's verification screens do not read these yet.

export function VerificationFormsView({ addToast, confirm, onBack }: {
  addToast: AddToast; confirm: (o: Omit<ConfirmState, "open">) => void; onBack: () => void;
}) {
  const [tab, setTab] = useState<FormSubject>("talent");
  const [saved, setSaved] = useState<Record<string, VerificationForm>>({});
  const [drafts, setDrafts] = useState<Drafts | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [dialog, setDialog] = useState<Dialog | null>(null);
  const drag = useRef<{ kind: "section"; from: number } | { kind: "field"; sectionId: string; from: number } | null>(null);

  const load = useCallback(() => {
    setLoadError(null);
    formsApi.list()
      .then((r) => {
        setSaved(r);
        setDrafts({ talent: (r.talent?.sections as FormSection[]) ?? [], organisation: (r.organisation?.sections as FormSection[]) ?? [] });
      })
      .catch((e) => setLoadError(e instanceof Error ? e.message : "Could not load the verification forms."));
  }, []);
  useEffect(load, [load]);

  const sections = drafts?.[tab] ?? [];
  const dirty = useMemo(() => {
    if (!drafts) return { talent: false, organisation: false };
    return {
      talent: JSON.stringify(drafts.talent) !== JSON.stringify(saved.talent?.sections ?? []),
      organisation: JSON.stringify(drafts.organisation) !== JSON.stringify(saved.organisation?.sections ?? []),
    };
  }, [drafts, saved]);
  const anyDirty = dirty.talent || dirty.organisation;

  // Edits are only held in this page until Save Changes, so say so before they are lost.
  useEffect(() => {
    if (!anyDirty) return;
    const warn = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [anyDirty]);

  const edit = (fn: (s: FormSection[]) => FormSection[]) => setDrafts((d) => (d ? { ...d, [tab]: fn(d[tab]) } : d));
  const editSection = (id: string, fn: (s: FormSection) => FormSection) => edit((list) => list.map((s) => (s.id === id ? fn(s) : s)));

  function save() {
    if (!drafts || saving) return;
    setSaving(true);
    formsApi.save(tab, drafts[tab])
      .then((row) => {
        setSaved((p) => ({ ...p, [tab]: row }));
        setDrafts((d) => (d ? { ...d, [tab]: row.sections } : d));
        addToast(`${tab === "talent" ? "Talent" : "Organisation"} form saved (version ${row.version})`, "success");
      })
      .catch((e) => addToast(e instanceof Error ? e.message : "Could not save the form", "error"))
      .finally(() => setSaving(false));
  }

  function leave() {
    if (!anyDirty) return onBack();
    confirm({ title: "Leave without saving?", body: "You have changes that have not been saved. They will be lost.", destructive: true, confirmLabel: "Leave", onConfirm: onBack });
  }

  const stats = formStats(sections);
  const version = saved[tab]?.version;

  // Drag and drop (mouse). The arrow buttons do the same on touch screens.
  const sectionDrag = (i: number) => ({
    start: () => { drag.current = { kind: "section", from: i }; },
    drop: () => {
      const d = drag.current;
      if (d?.kind === "section") edit((l) => move(l, d.from, i));
      drag.current = null;
    },
  });
  const fieldDrag = (sectionId: string) => (i: number) => ({
    start: () => { drag.current = { kind: "field", sectionId, from: i }; },
    drop: () => {
      const d = drag.current;
      if (d?.kind === "field" && d.sectionId === sectionId) editSection(sectionId, (s) => ({ ...s, fields: move(s.fields, d.from, i) }));
      drag.current = null;
    },
  });

  if (loadError) {
    return (
      <div>
        <PageHeader title="Verification Forms" sub="Manage verification forms for Talents and Organisations." />
        <div style={{ background: "#fee2e2", border: "1px solid #fecaca", color: "#991b1b", borderRadius: 10, padding: "12px 16px", fontSize: 13, fontFamily: F, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
          <span>{loadError}</span>
          <Btn variant="ghost" onClick={load}><RefreshCw size={12} /> Retry</Btn>
        </div>
      </div>
    );
  }
  if (!drafts) return <p style={{ fontSize: 13, color: T.muted, fontFamily: F }}>Loading verification forms…</p>;

  const dialogSection = dialog && dialog.kind === "section" && dialog.sectionId ? sections.find((s) => s.id === dialog.sectionId) : undefined;

  return (
    <div>
      <nav aria-label="Breadcrumb" style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: T.muted, fontFamily: F, marginBottom: 14 }}>
        <button onClick={leave} style={{ background: "none", border: "none", padding: 0, cursor: "pointer", color: T.muted, fontFamily: F, fontSize: 12 }}>Settings</button>
        <ChevronRight size={12} />
        <span style={{ color: T.text, fontWeight: 500 }}>Verification Forms</span>
      </nav>

      <PageHeader title="Verification Forms" sub="Manage verification forms for Talents and Organisations." actions={
        <Btn variant="primary" onClick={save} disabled={saving || !dirty[tab]}>
          <Check size={13} /> {saving ? "Saving…" : "Save Changes"}
        </Btn>
      } />

      {/* Stats */}
      <div className="vf-stats" style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 22 }}>
        {[["Sections", stats.sections], ["Total fields", stats.total], ["Required fields", stats.required], ["Optional fields", stats.optional]].map(([label, value]) => (
          <div key={label as string} style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 12, padding: "14px 20px", minWidth: 110 }}>
            <p style={{ fontSize: 20, fontWeight: 700, color: T.text, fontFamily: F, margin: 0, lineHeight: 1 }}>{value}</p>
            <p style={{ fontSize: 10, fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", color: T.muted, fontFamily: F, margin: "6px 0 0" }}>{label}</p>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap", marginBottom: 18 }}>
        <div role="tablist" className="vf-tabs" style={{ display: "inline-flex", background: T.s2, border: `1px solid ${T.border}`, borderRadius: 12, padding: 4, gap: 2 }}>
          {TABS.map((t) => {
            const on = t.id === tab;
            return (
              <button key={t.id} role="tab" aria-selected={on} onClick={() => setTab(t.id)}
                style={{ padding: "9px 20px", borderRadius: 9, border: "none", cursor: "pointer", fontFamily: F, fontSize: 13, fontWeight: on ? 600 : 500, background: on ? T.surface : "transparent", color: on ? T.text : T.sub, boxShadow: on ? "0 1px 3px rgba(0,0,0,0.08)" : "none" }}>
                {t.label}{dirty[t.id] ? " •" : ""}
              </button>
            );
          })}
        </div>
        <p style={{ fontSize: 11, color: T.muted, fontFamily: F, margin: 0 }}>
          {dirty[tab] ? "Unsaved changes" : version ? `Saved · version ${version}` : ""}
        </p>
      </div>

      {/* Sections */}
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        {sections.map((s, i) => (
          <SectionCard key={s.id} section={s} index={i} count={sections.length}
            dragSection={sectionDrag(i)} dragField={fieldDrag(s.id)}
            onMoveSection={(to) => edit((l) => move(l, i, to))}
            onEditSection={() => setDialog({ kind: "section", sectionId: s.id })}
            onDeleteSection={() => confirm({
              title: `Delete "${s.title}"?`,
              body: s.fields.length ? `This removes the section and its ${s.fields.length} ${s.fields.length === 1 ? "field" : "fields"} from the draft. Nothing changes for users until you save.` : "This removes the empty section from the draft.",
              destructive: true, confirmLabel: "Delete section",
              onConfirm: () => edit((l) => l.filter((x) => x.id !== s.id)),
            })}
            onAddField={() => setDialog({ kind: "field", sectionId: s.id, field: null })}
            onEditField={(f) => setDialog({ kind: "field", sectionId: s.id, field: f })}
            onDeleteField={(f) => confirm({
              title: `Delete "${f.label}"?`, body: "This removes the field from the draft. Nothing changes for users until you save.",
              destructive: true, confirmLabel: "Delete field",
              onConfirm: () => editSection(s.id, (sec) => ({ ...sec, fields: sec.fields.filter((x) => x.id !== f.id) })),
            })}
            onToggleRequired={(f) => editSection(s.id, (sec) => ({ ...sec, fields: sec.fields.map((x) => (x.id === f.id ? { ...x, required: !x.required } : x)) }))}
            onMoveField={(from, to) => editSection(s.id, (sec) => ({ ...sec, fields: move(sec.fields, from, to) }))} />
        ))}

        <button onClick={() => setDialog({ kind: "section", sectionId: null })}
          style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, padding: "18px", borderRadius: 14, border: `1.5px dashed ${T.border}`, background: "transparent", color: T.sub, fontFamily: F, fontSize: 13, cursor: "pointer" }}>
          <Plus size={15} /> Add Section
        </button>
      </div>

      {dialog?.kind === "section" && (
        <SectionModal isNew={!dialogSection} initial={{ title: dialogSection?.title ?? "", description: dialogSection?.description ?? "" }}
          onClose={() => setDialog(null)}
          onSave={(v) => {
            if (dialogSection) editSection(dialogSection.id, (s) => ({ ...s, ...v }));
            else edit((l) => [...l, { id: newId("sec"), ...v, fields: [] }]);
            setDialog(null);
          }} />
      )}
      {dialog?.kind === "field" && (
        <FieldModal isNew={!dialog.field} initial={dialog.field ?? blankField()} onClose={() => setDialog(null)}
          onSave={(f) => {
            editSection(dialog.sectionId, (s) => ({
              ...s,
              fields: dialog.field ? s.fields.map((x) => (x.id === f.id ? f : x)) : [...s.fields, f],
            }));
            setDialog(null);
          }} />
      )}
    </div>
  );
}
